import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { anchorTo, bindPopover } from '../../../lib/popoverAnchor';

/**
 * The "what is this?" affordance on a control.
 *
 * WHY IT EXISTS
 * These controls carry two levels of explanation in the metaMap and, until
 * now, showed neither. `hint` is a scannable fragment ("Date, location,
 * button labels") and `description` is the full sentence with the gotchas in
 * it -- the one on Dialog Background explains the whole light-island floor.
 * Both were parked on a native `title` attribute, which means: a ~1s delay,
 * no affordance saying help exists at all, nothing on touch, and no room for
 * a sentence that matters.
 *
 * Documentation is not the answer on its own. Most people will not go and
 * read it until they are already frustrated, which is one step too late for
 * a control named "Tab Content Inset".
 *
 * So the rail shows the hint inline as a byline, and this carries the full
 * description one interaction away.
 *
 * HOVER OPENS, CLICK PINS
 * Hover alone loses the text the moment you move toward it, and click alone
 * makes a help affordance feel like a commitment. Clicking pins it open so
 * the pointer can travel into the panel -- and so it works on touch, where
 * there is no hover at all.
 */
const TIP_W = 260;
const TIP_H = 150;

export default function InfoTip ({ label, children }) {
    const [open, setOpen] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [anchor, setAnchor] = useState(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const hoverTimer = useRef(null);

    const place = useCallback(() => {
        setAnchor(anchorTo(triggerRef.current, TIP_W, TIP_H));
    }, []);

    const close = useCallback(() => {
        setOpen(false);
        setPinned(false);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        place();
        return bindPopover({
            onDismiss: close,
            onReplace: place,
            ignore: [triggerRef, panelRef],
        });
    }, [open, place, close]);

    useEffect(() => () => clearTimeout(hoverTimer.current), []);

    // A short delay, so dragging the pointer across a column of controls does
    // not flash a tip on every one it crosses.
    const handleEnter = useCallback(() => {
        clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => setOpen(true), 250);
    }, []);

    const handleLeave = useCallback(() => {
        clearTimeout(hoverTimer.current);
        if (!pinned) setOpen(false);
    }, [pinned]);

    const handleClick = useCallback((ev) => {
        ev.preventDefault();
        clearTimeout(hoverTimer.current);
        if (pinned) {
            close();
        } else {
            setOpen(true);
            setPinned(true);
        }
    }, [pinned, close]);

    return (
        <span className="theme-infotip">
            <button
                type="button"
                ref={triggerRef}
                className="theme-infotip__trigger"
                aria-expanded={open}
                aria-label={'About ' + label}
                onClick={handleClick}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                // Keyboard users get the same thing hover gives, rather than
                // having to know the button is also clickable.
                onFocus={() => setOpen(true)}
                onBlur={() => { if (!pinned) setOpen(false); }}
            >
                <i className="fa fa-question-circle" aria-hidden="true"/>
            </button>

            {open && anchor ? createPortal(
                <div
                    className="theme-infotip__panel"
                    ref={panelRef}
                    role="tooltip"
                    style={{ left: anchor.left + 'px', top: anchor.top + 'px' }}
                    onMouseEnter={() => clearTimeout(hoverTimer.current)}
                    onMouseLeave={handleLeave}
                >
                    <strong className="theme-infotip__title">{label}</strong>
                    <p className="theme-infotip__body">{children}</p>
                </div>,
                document.body
            ) : null}
        </span>
    );
}
